// per-chain current/previous CustodialWalletManager pair, the single place the
// FE declares these addresses.
// IN-CODE ONLY: never read these from env (`import.meta.env`) or any runtime
// config.
//
// At switchover an operator sets `current` to the NEW manager and `previous` to
// the OLD beta manager. While `previous` is set, the legacy-withdraw banner
// (features/custodial/components/LegacyWalletWithdraw.tsx) offers users a
// self-serve withdrawal from their old wallet: the connected EOA owns that
// wallet and calls its owner-only `withdrawNative` / `withdrawErc20` itself, so
// no backend, no signer and no contract change is involved. Set `previous` back
// to null to retire the feature.
//
// MoonHub.API and RustSigner keep their OWN copies of `current` and must be
// flipped in lockstep (their config sites: MoonHub.API/access/EvmChainAccess.ts,
// RustSigner/src/config.rs). Only this repo tracks `previous`.
//
// While `previous` is null (or equal to `current`) every legacy code path is
// inert: the eligibility hook stays disabled and issues zero RPC reads.

export interface CustodialManagerPair {
    current: `0x${string}`;
    previous: `0x${string}` | null;
}

export const ZERO_CUSTODIAL_MANAGER_ADDRESS =
    '0x0000000000000000000000000000000000000000' as const;

export const CUSTODIAL_WALLET_MANAGERS: Record<number, CustodialManagerPair> = {
    25: { current: '0x35b9fD3D9eaabc996DDe4D3f3614254fCf2aBAB7', previous: null },
    999: { current: '0xa1f1C13DC56f3422675A8ebC74A6a811b6a5984F', previous: null },
    // Robinhood Chain, from SmartContracts.Sensitive/deploy/data/deployments.csv
    // (4663). A chain added here before its manager is deployed keeps the zero
    // address as a deliberate tripwire: `custodialManagerFor` throws rather
    // than letting anything be signed against a manager that does not exist.
    4663: { current: '0x36eE0f77323c090D05fF1bFB2638E9eb5DB1E060', previous: null },
};

export const CRONOS_CUSTODIAL_WALLET_MANAGER =
    CUSTODIAL_WALLET_MANAGERS[25].current;
export const HYPER_EVM_CUSTODIAL_WALLET_MANAGER =
    CUSTODIAL_WALLET_MANAGERS[999].current;

/** False while a chain's manager is still the zero placeholder. */
export function hasCustodialManager(chainId: number): boolean {
    const current = CUSTODIAL_WALLET_MANAGERS[chainId]?.current;
    return (
        typeof current === 'string' &&
        current.toLowerCase() !== ZERO_CUSTODIAL_MANAGER_ADDRESS
    );
}

/**
 * The manager an anchor's statements are scoped to. It is the EIP-712
 * `verifyingContract` of everything the user signs there, so a chain with no
 * deployment must fail loudly here instead of producing a signature bound to
 * the zero address.
 */
export function custodialManagerFor(chainId: number): `0x${string}` {
    const pair = CUSTODIAL_WALLET_MANAGERS[chainId];
    if (!pair) {
        throw new Error(
            `No CustodialWalletManager is configured for chain ${chainId}.`,
        );
    }
    if (!hasCustodialManager(chainId)) {
        throw new Error(
            `The CustodialWalletManager for chain ${chainId} is not deployed ` +
                'yet, so nothing can be signed against it.',
        );
    }
    return pair.current;
}

/** One ERC20 the legacy balance check looks for on the old custodial wallet. */
export interface LegacyBalanceToken {
    address: `0x${string}`;
    symbol: string;
    decimals: number;
}

/**
 * Candidate tokens the legacy-withdraw banner checks on a user's OLD custodial
 * wallet, per chain. Deliberately a short hand-kept list rather than a discovery
 * scan: the audience is a handful of beta users and the only assets a Snappy
 * wallet ever held are the trading collateral plus wrapped native.
 *
 * Native (HYPE, 18 decimals) is always checked and is not listed here.
 */
export const LEGACY_BALANCE_TOKENS: Record<number, LegacyBalanceToken[]> = {
    25: [],
    4663: [],
    999: [
        {
            address: '0xb88339CB7199b77E23DB6E890353E22632Ba630f',
            symbol: 'USDC',
            decimals: 6,
        },
        {
            address: '0x5555555555555555555555555555555555555555',
            symbol: 'WHYPE',
            decimals: 18,
        },
    ],
};

export function previousCustodialManager(
    chainId: number,
): `0x${string}` | null {
    const pair = CUSTODIAL_WALLET_MANAGERS[chainId] as
        | CustodialManagerPair
        | undefined;
    if (!pair?.previous) return null;
    // a previous equal to current means the switchover entry was never updated — treat it as unset
    return pair.previous.toLowerCase() !== pair.current.toLowerCase()
        ? pair.previous
        : null;
}
